import type { NavConfig, NavSubItem } from './types';
import type { Library } from '@/types/catalog';

/**
 * Build the org-admin navigation.
 *
 * @param libraries  The libraries the signed-in admin can access (from
 *   GET /api/me/libraries via the accessible-library store), already ordered
 *   for display. The store holds an empty list until its fetch resolves, so the
 *   Content Library has no sub-items until then.
 */
export function buildOrgAdminNav(libraries: readonly Library[]): NavConfig {
  const librarySubItems = libraries.map<NavSubItem>(l => ({
    to: `/admin/libraries/${l.id}`,
    label: l.effectiveDisplayName,
  }));

  return {
    items: [
      {
        to: '/admin',
        icon: 'dashboard',
        label: 'Dashboard',
      },
      {
        to: '/admin/library',
        icon: 'library_books',
        label: 'Content Library',
        expandable: true,
        // Parent `/admin/library` already routes to "All Content" — no duplicate.
        subItems: librarySubItems,
      },
      {
        to: '/admin/pathways',
        icon: 'route',
        label: 'Pathways',
        expandable: true,
        // Parent `/admin/pathways` already routes to "All Pathways" — no duplicate.
        subItems: [
          { to: '/admin/pathways/custom', label: 'Custom Pathways' },
          { to: '/admin/pathways/barbri', label: 'BARBRI Pathways' },
          { to: '/admin/pathways/create', label: 'Create a Pathway' },
        ],
      },
      {
        to: '/admin/users',
        icon: 'people',
        label: 'People',
        expandable: true,
        // Users is listed explicitly so the sub-nav matches what's actually behind it.
        // The People parent still routes to /admin/users for muscle-memory continuity.
        subItems: [
          { to: '/admin/users', label: 'Users' },
          { to: '/admin/groups', label: 'Groups' },
          { to: '/admin/admins', label: 'Admins' },
        ],
      },
      {
        to: '/admin/activity-report',
        icon: 'assessment',
        label: 'Activity Report',
      },
    ],
  };
}
